import { useState, useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useSearchProduct } from '../hooks/useSearchProduct'
import { Card } from '../ui/Card'
import { ProductsListSkeleton } from '../ui/ProductsListSkelton'
import { NotifyMeForm } from './Products/NotifyMeForm'
import { Helmet } from 'react-helmet-async'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/auth-context'

type SetQueried = (query: string) => void
interface DiscoverProps {
  query: string
  setQueried: SetQueried
  onClearSearch: () => void
  categoryId?: string | null
}

export const Discover: React.FC<DiscoverProps> = ({
  query,
  setQueried,
  onClearSearch,
  categoryId,
}) => {
  const { products, error, isError, isLoading } = useSearchProduct(
    query,
    categoryId,
  )
  const [showForm, setShowForm] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()

  // Check if user is returning from login with form data
  useEffect(() => {
    if (location.state?.from === 'product-request' && user) {
      // User came back from login, open the form
      // Note: The search query is already restored in MainPage component
      setIsVisible(true)
      setShowForm(true)

      // Clear the state to prevent reopening on subsequent renders
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, location.pathname, navigate, user])

  const handleOpenForm = () => {
    // Check if user is authenticated
    if (!user) {
      // Redirect to login with query as product name
      navigate('/login', {
        state: {
          from: 'product-request',
          productName: query,
        },
      })
      return
    }

    // User is authenticated, show the form
    setIsVisible(true)
    setShowForm(true)
  }

  const closeShowForm = () => {
    setShowForm(false)
    onClearSearch()
  }

  const handleExitComplete = () => {
    setIsVisible(false) // now we actually unmount it
  }

  return (
    <div
      className='mt-8 p-4 rounded-md border'
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderColor: 'var(--border-color)',
      }}
    >
      {error && <h3>{error.message}</h3>}
      {isLoading && <ProductsListSkeleton />}

      {products?.length === 0 && (
        <div>
          {!showForm && (
            <>
              <p className='col-span-full text-lg font-bold text-center'>
                کتاب مورد نظر شما پیدا نشد!
                <br /> شاید اسم را کمی تغییر دهید یا کتاب دیگری را امتحان کنید.
                <br />
                اگر همچنان دنبال همین کتاب هستید، با کلیک روی دکمه زیر، فرم
                درخواست را پر کنید تا به محض موجود شدن، به شما اطلاع بدهیم.
              </p>
              <div className='flex justify-center mt-4'>
                <button
                  className='w-full md:w-1/2 lg:w-1/3 xl:w-1/5 font-bold text-xl text-white text-center p-3 rounded flex items-center justify-center'
                  style={{ backgroundColor: 'var(--accent-primary)' }}
                  onClick={handleOpenForm}
                >
                  می خوام با خبر بشم!
                </button>
              </div>
            </>
          )}

          <AnimatePresence onExitComplete={handleExitComplete}>
            {showForm && (
              <motion.div
                key='notify-form'
                initial={{ opacity: 0, y: -40, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -40, scale: 0.98 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className='w-full max-w-lg mx-auto mt-8'
              >
                <NotifyMeForm
                  defaultProductName={query}
                  closeShowForm={closeShowForm}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      <div className='grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mt-3'>
        <Helmet>
          <title className='text-xl font-semibold'>
            بوک استوری - داستان کتاب
          </title>
          <meta
            name='description'
            content='بوک استوری - هر کتاب یک داستان، هر داستان یک زندگی دوباره'
          />
        </Helmet>
        {products &&
          products.map((item) => (
            <Card
              key={item.id}
              id={item.id}
              title={item.title}
              author={item.attributes.author}
              imageUrl={item.images[0]}
              price={item.price}
              compareAtPrice={item.compareAtPrice}
            />
          ))}
      </div>
    </div>
  )
}
